import type { ScoToolSession } from '@src/graphql-types'
import moment from 'moment'

/**
 * Minimal ScoToolSession-shaped input for list/toolbar titles.
 * Partial query results are accepted (same field paths as ScoToolSession).
 */
export type WilSessionTitleSource = Partial<
  Pick<
    ScoToolSession,
    'id' | 'toolSessionId' | 'title' | 'generatedSummary' | 'createdAt' | 'updatedAt' | 'threadId'
  >
> & {
  thread?: {
    id?: string | null
    threadId?: string | null
    lastActiveAt?: string | null
    commentsCount?: number | null
    comments?: {
      items?: Array<{
        id?: string | null
        body?: string | null
        createdByUserId?: string | null
        createdByUser?: { id?: string | null; userId?: string | null } | null
      } | null> | null
    } | null
  } | null
}

/** Generic titles we used to stamp on create — treat as empty so real content can win. */
const PLACEHOLDER_TITLE_RE = /^(new chat( with wil(\.ai)?)?|untitled( chat| conversation)?|chat\s*#?\d+)$/i

const TITLE_MAX_LEN = 72

export function isMeaningfulTitle(title?: string | null): boolean {
  const t = title?.trim()
  if (!t) {
    return false
  }
  return !PLACEHOLDER_TITLE_RE.test(t)
}

export function stripMessageBody(body?: string | null): string {
  if (!body) {
    return ''
  }
  return body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/[#>*_~]+/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function clipTitle(text: string, max = TITLE_MAX_LEN): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (!clean) {
    return ''
  }
  if (clean.length <= max) {
    return clean
  }
  return `${clean.slice(0, max - 1).trimEnd()}…`
}

/** Empty / brand-new sessions: time if today, else short date. */
export function formatSessionWhen(when?: string | null): string {
  if (!when) {
    return 'New conversation'
  }
  const m = moment(when)
  if (!m.isValid()) {
    return 'New conversation'
  }
  if (m.isSame(moment(), 'day')) {
    return `Today, ${m.format('h:mm A')}`
  }
  if (m.isSame(moment().subtract(1, 'day'), 'day')) {
    return `Yesterday, ${m.format('h:mm A')}`
  }
  if (m.isSame(moment(), 'year')) {
    return m.format('MMM D')
  }
  return m.format('MMM D, YYYY')
}

export function firstHumanMessage(session: WilSessionTitleSource, toolUserId?: string | null): string | null {
  const items = session.thread?.comments?.items || []
  for (const comment of items) {
    if (!comment) {
      continue
    }
    const authorId = comment.createdByUserId || comment.createdByUser?.userId
    if (toolUserId && authorId && authorId === toolUserId) {
      continue
    }
    const text = stripMessageBody(comment.body)
    if (text) {
      return text
    }
  }
  // Fallback: any first non-empty message (e.g. if toolUserId unknown)
  for (const comment of items) {
    const text = stripMessageBody(comment?.body)
    if (text) {
      return text
    }
  }
  return null
}

/**
 * Topic when we can, time when we can't:
 * meaningful title → generated summary → first human message → date/time.
 */
export function sessionTitle(
  session: WilSessionTitleSource,
  toolUserId?: string | null,
  maxLen = TITLE_MAX_LEN
): string {
  if (isMeaningfulTitle(session.title)) {
    return clipTitle(session.title!.trim(), maxLen)
  }

  if (session.generatedSummary?.trim()) {
    const line = session.generatedSummary.trim().split(/\n/)[0]
    const clipped = clipTitle(line, maxLen)
    if (clipped) {
      return clipped
    }
  }

  const firstMsg = firstHumanMessage(session, toolUserId)
  if (firstMsg) {
    return clipTitle(firstMsg, maxLen)
  }

  return formatSessionWhen(session.thread?.lastActiveAt || session.updatedAt || session.createdAt)
}

export function sessionSubtitle(session: WilSessionTitleSource, displayName: string): string {
  const when = session.thread?.lastActiveAt || session.updatedAt || session.createdAt
  if (!when) {
    return displayName
  }
  return `${displayName} · ${moment(when).fromNow()}`
}
